// src/components/reportes/ModalBoletines.js
import React, { useState, useRef } from 'react';
import { X, FileText, Download, Calendar, Settings, Image as ImageIcon, Loader, MapPin } from 'lucide-react';
import html2pdf from 'html2pdf.js';

const ModalBoletines = ({ isOpen, onClose, emergencias, eventos, departamentos }) => {
  const [paso, setPaso] = useState(1); 
  const [tipoBoletin, setTipoBoletin] = useState(null); 
  const [configuracion, setConfiguracion] = useState({
    fechaInicio: new Date().toISOString().split('T')[0],
    fechaFin: new Date().toISOString().split('T')[0],
    departamentos: [],
    eventos: [],
    tiposEmergencia: [],
    tiposRed: [],
    incluirImagenes: true
  });
  const [generando, setGenerando] = useState(false);
  const boletinRef = useRef(null);

  if (!isOpen) return null;

  // Formatear fecha
  const formatearFecha = (fechaString) => {
    if (!fechaString) return 'N/A';
    try {
      const fecha = new Date(fechaString);
      return fecha.toLocaleDateString('es-HN', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch {
      return fechaString;
    }
  };

  // Obtener emergencias filtradas
  const obtenerEmergenciasFiltradas = () => {
    if (!emergencias || !Array.isArray(emergencias)) return [];
    
    let filtradas = emergencias;

    // Filtrar por fechas
    filtradas = filtradas.filter(e => {
      const fechaEmergencia = e.Fecha || e.FechaCreacion;
      if (!fechaEmergencia) return false;
      
      const partes = fechaEmergencia.split('-');
      let fechaFormateada;
      
      if (partes[0].length === 2) {
        fechaFormateada = `${partes[2]}-${partes[1]}-${partes[0]}`;
      } else {
        fechaFormateada = fechaEmergencia.split(' ')[0];
      }
      
      return fechaFormateada >= configuracion.fechaInicio && fechaFormateada <= configuracion.fechaFin;
    });

    // Filtrar por departamentos (si hay seleccionados)
    if (configuracion.departamentos.length > 0) {
      filtradas = filtradas.filter(e => 
        configuracion.departamentos.some(deptCode => e.MunicipioFK?.startsWith(deptCode))
      );
    }

    // Filtrar por eventos (si hay seleccionados)
    if (configuracion.eventos.length > 0) {
      filtradas = filtradas.filter(e => 
        configuracion.eventos.includes(String(e.EventoEmergenciaFK))
      );
    }

    // Filtrar por tipos de emergencia (si hay seleccionados)
    if (configuracion.tiposEmergencia.length > 0) {
      filtradas = filtradas.filter(e => 
        configuracion.tiposEmergencia.includes(e.NombreTipoEmergencia)
      );
    }

    // Filtrar por tipos de red (si hay seleccionados)
    if (configuracion.tiposRed.length > 0) {
      filtradas = filtradas.filter(e => 
        configuracion.tiposRed.includes(e.TipoRed)
      );
    }

    return filtradas;
  };

  // Agrupar por departamento
  const agruparPorDepartamento = (emergenciasList) => {
    if (!emergenciasList || !Array.isArray(emergenciasList)) return [];
    
    const grupos = {};
    
    emergenciasList.forEach(e => {
      const deptCode = e.MunicipioFK?.substring(0, 2);
      const deptNombre = e.NombreMunicipio?.split(',')[1]?.trim() || 'Desconocido';
      
      if (!grupos[deptCode]) {
        grupos[deptCode] = {
          codigo: deptCode,
          nombre: deptNombre,
          emergencias: [],
          total: 0,
          atendidas: 0,
          enAtencion: 0,
          porAtender: 0,
          municipios: new Set(),
          kmIncomunicados: 0
        };
      }
      
      grupos[deptCode].emergencias.push(e);
      grupos[deptCode].total++;
      grupos[deptCode].municipios.add(e.MunicipioFK);
      
      if (e.Estado === 'Atendido') grupos[deptCode].atendidas++;
      else if (e.Estado === 'En Atención') grupos[deptCode].enAtencion++;
      else grupos[deptCode].porAtender++;
      
      if (e.LongitudTramo) grupos[deptCode].kmIncomunicados += parseFloat(e.LongitudTramo) || 0;
    });
    
    return Object.values(grupos).sort((a, b) => b.total - a.total);
  };

  // Obtener imágenes
  const obtenerImagenes = (emergencia) => {
    if (!emergencia.Anexo || !configuracion.incluirImagenes) return [];
    
    if (typeof emergencia.Anexo === 'string' && emergencia.Anexo.trim() !== '') {
      const urls = emergencia.Anexo.split(',').map(url => url.trim()).filter(url => url !== '');
      return urls.slice(0, 4);
    }
    
    return [];
  };

  // Generar PDF
  const generarPDF = async () => {
    if (!boletinRef.current) return;
    
    setGenerando(true);
    
    try {
      const opciones = {
        margin: [8, 8, 8, 8],
        filename: `Boletin_Emergencias_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2.5,
          useCORS: true,
          logging: false,
          letterRendering: true,
          backgroundColor: '#ffffff'
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'letter', 
          orientation: 'landscape' 
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      await html2pdf().set(opciones).from(boletinRef.current).save();
      alert('✅ Boletín generado exitosamente');
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('❌ Error al generar el PDF');
    } finally {
      setGenerando(false);
    }
  };

  const emergenciasFiltradas = obtenerEmergenciasFiltradas();
  const departamentosAgrupados = agruparPorDepartamento(emergenciasFiltradas);
  
  const stats = {
    total: emergenciasFiltradas.length,
    atendidas: emergenciasFiltradas.filter(e => e.Estado === 'Atendido').length,
    enAtencion: emergenciasFiltradas.filter(e => e.Estado === 'En Atención').length,
    porAtender: emergenciasFiltradas.filter(e => e.Estado === 'Por Atender').length,
    departamentos: new Set(emergenciasFiltradas.map(e => e.MunicipioFK?.substring(0, 2))).size,
    municipios: new Set(emergenciasFiltradas.map(e => e.MunicipioFK)).size,
    kmIncomunicados: emergenciasFiltradas.reduce((sum, e) => sum + (parseFloat(e.LongitudTramo) || 0), 0),
    kmAtendidos: emergenciasFiltradas.filter(e => e.Estado === 'Atendido').reduce((sum, e) => sum + (parseFloat(e.LongitudTramo) || 0), 0)
  };

  const tiposEmergenciaUnicos = emergencias && Array.isArray(emergencias) 
    ? [...new Set(emergencias.map(e => e.NombreTipoEmergencia))].filter(Boolean)
    : [];
  const tiposRedUnicos = emergencias && Array.isArray(emergencias)
    ? [...new Set(emergencias.map(e => e.TipoRed))].filter(Boolean)
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-7xl max-h-[95vh] overflow-hidden bg-white rounded-xl shadow-2xl">
        
        {/* HEADER */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 h-12 bg-gray-900 rounded-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Generador de Boletines</h2>
              <p className="text-sm text-gray-600">Fondo Cafetero Nacional</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 transition-colors rounded-lg hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* CONTENIDO */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(95vh - 88px)' }}>
          
          {/* PASO 1: Selección de Tipo */}
          {paso === 1 && (
            <div className="p-8">
              <div className="max-w-4xl mx-auto">
                <div className="mb-8 text-center">
                  <h3 className="mb-2 text-2xl font-bold text-gray-900">Seleccione el Tipo de Boletín</h3>
                  <p className="text-gray-600">Escoja el formato que mejor se adapte a sus necesidades</p>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  {/* Boletín Diario */}
                  <button
                    onClick={() => {
                      setTipoBoletin('diario');
                      setConfiguracion({
                        ...configuracion,
                        fechaInicio: new Date().toISOString().split('T')[0],
                        fechaFin: new Date().toISOString().split('T')[0]
                      });
                      setPaso(3);
                    }}
                    className="p-8 transition-all border-2 border-gray-200 rounded-xl hover:border-gray-900 hover:shadow-lg group"
                  >
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 transition-colors bg-gray-100 rounded-full group-hover:bg-gray-900">
                      <Calendar className="w-8 h-8 text-gray-700 transition-colors group-hover:text-white" />
                    </div>
                    <h4 className="mb-2 text-xl font-bold text-gray-900">Boletín Diario</h4>
                    <p className="text-sm text-gray-600">
                      Genera un reporte automático de todas las emergencias del día actual
                    </p>
                  </button>

                  {/* Boletín Personalizado */}
                  <button
                    onClick={() => {
                      setTipoBoletin('personalizado');
                      setPaso(2);
                    }}
                    className="p-8 transition-all border-2 border-gray-200 rounded-xl hover:border-gray-900 hover:shadow-lg group"
                  >
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 transition-colors bg-gray-100 rounded-full group-hover:bg-gray-900">
                      <Settings className="w-8 h-8 text-gray-700 transition-colors group-hover:text-white" />
                    </div>
                    <h4 className="mb-2 text-xl font-bold text-gray-900">Boletín Personalizado</h4>
                    <p className="text-sm text-gray-600">
                      Configure fechas, departamentos y tipos de emergencia según sus necesidades
                    </p>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* PASO 2: Configuración */}
          {paso === 2 && (
            <div className="p-8">
              <div className="max-w-5xl mx-auto">
                <div className="mb-8">
                  <h3 className="mb-2 text-2xl font-bold text-gray-900">Configurar Boletín</h3>
                  <p className="text-gray-600">Personalice los filtros y parámetros del reporte</p>
                </div>

                <div className="space-y-6">
                  
                  {/* Rango de Fechas */}
                  <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
                    <h4 className="mb-4 text-lg font-semibold text-gray-900">Período de Reporte</h4>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Fecha Inicio</label>
                        <input
                          type="date"
                          value={configuracion.fechaInicio}
                          onChange={(e) => setConfiguracion({...configuracion, fechaInicio: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Fecha Fin</label>
                        <input
                          type="date"
                          value={configuracion.fechaFin}
                          onChange={(e) => setConfiguracion({...configuracion, fechaFin: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Departamentos */}
                  <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
                    <h4 className="mb-4 text-lg font-semibold text-gray-900">Filtrar por Departamento</h4>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                      {departamentos.map(dept => (
                        <label key={dept.DepartamentoID} className="flex items-center p-3 space-x-2 transition-colors border border-gray-200 rounded-lg cursor-pointer hover:bg-white">
                          <input
                            type="checkbox"
                            checked={configuracion.departamentos.includes(dept.DepartamentoID)}
                            onChange={(e) => {
                              const newDepts = e.target.checked
                                ? [...configuracion.departamentos, dept.DepartamentoID]
                                : configuracion.departamentos.filter(d => d !== dept.DepartamentoID);
                              setConfiguracion({...configuracion, departamentos: newDepts});
                            }}
                            className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                          />
                          <span className="text-sm font-medium text-gray-700">{dept.NombreDepartamento}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Tipos de Emergencia */}
                  <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
                    <h4 className="mb-4 text-lg font-semibold text-gray-900">Tipos de Emergencia</h4>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                      {tiposEmergenciaUnicos.map(tipo => (
                        <label key={tipo} className="flex items-center p-3 space-x-2 transition-colors border border-gray-200 rounded-lg cursor-pointer hover:bg-white">
                          <input
                            type="checkbox"
                            checked={configuracion.tiposEmergencia.includes(tipo)}
                            onChange={(e) => {
                              const newTipos = e.target.checked
                                ? [...configuracion.tiposEmergencia, tipo]
                                : configuracion.tiposEmergencia.filter(t => t !== tipo);
                              setConfiguracion({...configuracion, tiposEmergencia: newTipos});
                            }}
                            className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                          />
                          <span className="text-sm font-medium text-gray-700">{tipo}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Tipos de Red */}
                  <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
                    <h4 className="mb-4 text-lg font-semibold text-gray-900">Tipos de Red Vial</h4>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                      {tiposRedUnicos.map(tipo => (
                        <label key={tipo} className="flex items-center p-3 space-x-2 transition-colors border border-gray-200 rounded-lg cursor-pointer hover:bg-white">
                          <input
                            type="checkbox"
                            checked={configuracion.tiposRed.includes(tipo)}
                            onChange={(e) => {
                              const newTipos = e.target.checked
                                ? [...configuracion.tiposRed, tipo]
                                : configuracion.tiposRed.filter(t => t !== tipo);
                              setConfiguracion({...configuracion, tiposRed: newTipos});
                            }}
                            className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                          />
                          <span className="text-sm font-medium text-gray-700">{tipo}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Opciones Adicionales */}
                  <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
                    <h4 className="mb-4 text-lg font-semibold text-gray-900">Opciones de Contenido</h4>
                    <label className="flex items-center p-3 space-x-3 transition-colors border border-gray-200 rounded-lg cursor-pointer hover:bg-white">
                      <input
                        type="checkbox"
                        checked={configuracion.incluirImagenes}
                        onChange={(e) => setConfiguracion({...configuracion, incluirImagenes: e.target.checked})}
                        className="w-5 h-5 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                      />
                      <div className="flex items-center gap-2">
                        <ImageIcon className="w-5 h-5 text-gray-600" />
                        <span className="font-medium text-gray-700">Incluir imágenes de emergencias</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Botones de Navegación */}
                <div className="flex gap-4 mt-8">
                  <button
                    onClick={() => setPaso(1)}
                    className="px-6 py-3 font-medium text-gray-700 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    ← Volver
                  </button>
                  <button
                    onClick={() => setPaso(3)}
                    className="flex-1 px-6 py-3 font-medium text-white transition-colors bg-gray-900 rounded-lg hover:bg-gray-800"
                  >
                    Generar Vista Previa →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* PASO 3: Vista Previa y Generación */}
          {paso === 3 && (
            <div className="p-8 bg-gray-100">
              <div className="max-w-6xl mx-auto">
                
                {/* Barra de Acciones */}
                <div className="flex items-center justify-between p-4 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Vista Previa del Boletín</h3>
                    <p className="text-sm text-gray-600">
                      {emergenciasFiltradas.length} emergencias encontradas | {departamentosAgrupados.length} departamentos
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setPaso(tipoBoletin === 'personalizado' ? 2 : 1)}
                      className="px-5 py-2 font-medium text-gray-700 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      ← Volver
                    </button>
                    <button
                      onClick={generarPDF}
                      disabled={generando}
                      className="flex items-center gap-2 px-6 py-2 font-medium text-white transition-colors bg-gray-900 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {generando ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Generando...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5" />
                          Descargar PDF
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* CONTENIDO DEL BOLETÍN */}
                <div className="bg-white shadow-lg">
                  <div ref={boletinRef} className="bg-white">
                    
                    {/* PORTADA EJECUTIVA */}
                    <div className="p-8">
                      
                      {/* Header Limpio */}
                      <div className="flex items-center justify-between pb-4 mb-6 border-b-3 border-black">
                        <div className="flex items-center gap-3">
                          <img 
                            src="/logo192.png" 
                            alt="FCN" 
                            className="w-14 h-14 object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <h1 className="text-2xl font-bold text-black">FONDO CAFETERO NACIONAL</h1>
                        </div>
                        <img 
                          src="/logo192.png" 
                          alt="FCN" 
                          className="w-14 h-14 object-contain opacity-10"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>

                      {/* Título Central */}
                      <div className="mb-6 text-center">
                        <div className="inline-block px-8 py-3 mb-3 border-3 border-black">
                          <h2 className="text-xl font-bold text-black">RESUMEN DE ATENCIÓN DE EMERGENCIAS</h2>
                        </div>
                        <p className="text-base font-semibold text-gray-800">EN LA RED VIAL CAFETALERA</p>
                        <p className="mt-3 text-lg font-bold text-black">
                          {formatearFecha(configuracion.fechaInicio)}
                          {configuracion.fechaInicio !== configuracion.fechaFin && 
                            ` - ${formatearFecha(configuracion.fechaFin)}`
                          }
                        </p>
                      </div>

                      {/* DATOS PRINCIPALES */}
                      <div className="mb-6">
                        <h3 className="pb-3 mb-5 text-center text-base font-bold text-black border-b-3 border-black">
                          DATOS DE ATENCIÓN EN LA RED VIAL CAFETALERA
                        </h3>
                        
                        <div className="grid grid-cols-3 gap-5">
                          
                          {/* COLUMNA 1 - MUNICIPIOS */}
                          <div className="space-y-3">
                            <div className="p-3 text-center bg-gray-100">
                              <p className="mb-1 text-xs font-bold text-gray-700">DEPARTAMENTOS CON EMERGENCIA</p>
                              <p className="text-4xl font-bold text-black">{stats.departamentos}</p>
                            </div>
                            <div className="p-3 text-center bg-gray-100">
                              <p className="mb-1 text-xs font-bold text-gray-700">MUNICIPIOS REPORTADOS</p>
                              <p className="text-4xl font-bold text-black">{stats.municipios}</p>
                            </div>
                            <div className="p-3 text-center bg-green-100">
                              <p className="mb-1 text-xs font-bold text-green-800">MUNICIPIOS ATENDIDOS</p>
                              <p className="text-4xl font-bold text-green-900">
                                {new Set(emergenciasFiltradas.filter(e => e.Estado === 'Atendido').map(e => e.MunicipioFK)).size}
                              </p>
                            </div>
                            <div className="p-3 text-center bg-blue-100">
                              <p className="mb-1 text-xs font-bold text-blue-800">MUNICIPIOS EN ATENCIÓN</p>
                              <p className="text-4xl font-bold text-blue-900">
                                {new Set(emergenciasFiltradas.filter(e => e.Estado === 'En Atención').map(e => e.MunicipioFK)).size}
                              </p>
                            </div>
                            <div className="p-3 text-center bg-orange-100">
                              <p className="mb-1 text-xs font-bold text-orange-800">MUNICIPIOS POR ATENDER</p>
                              <p className="text-4xl font-bold text-orange-900">
                                {new Set(emergenciasFiltradas.filter(e => e.Estado === 'Por Atender').map(e => e.MunicipioFK)).size}
                              </p>
                            </div>
                          </div>

                          {/* COLUMNA 2 - EMERGENCIAS */}
                          <div className="space-y-3">
                            <div className="p-3 text-center bg-gray-100">
                              <p className="mb-1 text-xs font-bold text-gray-700">EMERGENCIAS REPORTADAS</p>
                              <p className="text-4xl font-bold text-black">{stats.total}</p>
                            </div>
                            <div className="p-3 text-center bg-green-100">
                              <p className="mb-1 text-xs font-bold text-green-800">EMERGENCIAS ATENDIDAS</p>
                              <p className="text-4xl font-bold text-green-900">{stats.atendidas}</p>
                            </div>
                            <div className="p-3 text-center bg-blue-100">
                              <p className="mb-1 text-xs font-bold text-blue-800">EMERGENCIAS EN ATENCIÓN</p>
                              <p className="text-4xl font-bold text-blue-900">{stats.enAtencion}</p>
                            </div>
                            <div className="p-3 text-center bg-orange-100">
                              <p className="mb-1 text-xs font-bold text-orange-800">EMERGENCIAS POR ATENDER</p>
                              <p className="text-4xl font-bold text-orange-900">{stats.porAtender}</p>
                            </div>
                          </div>

                          {/* COLUMNA 3 - KILÓMETROS */}
                          <div className="flex items-center">
                            <div className="w-full p-6 bg-gray-100 border-3 border-black">
                              <p className="mb-5 text-center text-base font-bold text-black">KILÓMETROS</p>
                              <div className="mb-5 text-center">
                                <p className="text-sm font-semibold text-gray-700">Incomunicados</p>
                                <p className="text-5xl font-bold text-black">{Math.round(stats.kmIncomunicados)}</p>
                              </div>
                              <div className="h-0.5 mb-5 bg-gray-400"></div>
                              <div className="text-center">
                                <p className="text-sm font-semibold text-gray-700">Atendidos</p>
                                <p className="text-5xl font-bold text-black">{Math.round(stats.kmAtendidos)}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="pt-4 mt-6 text-center border-t border-gray-400">
                        <p className="text-sm font-bold text-gray-700">www.fondocafetero.com</p>
                        <p className="text-xs text-gray-600">Sistema de Gestión de Emergencias - FCN</p>
                      </div>
                    </div>

                    {/* DETALLE POR DEPARTAMENTO */}
                    {departamentosAgrupados && departamentosAgrupados.length > 0 && departamentosAgrupados.map((dept, deptIndex) => (
                      <div key={dept.codigo} className="p-8" style={{ pageBreakBefore: 'always' }}>
                        
                        {/* Header del Departamento */}
                        <div className="pb-3 mb-6 border-b-4 border-gray-900">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <img 
                                src="/logo192.png" 
                                alt="FCN" 
                                className="w-10 h-10 object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div>
                                <h2 className="text-2xl font-bold text-gray-900">{dept.nombre}</h2>
                                <div className="inline-block px-3 py-1 mt-1 text-xs font-semibold text-gray-700 bg-gray-200 rounded">
                                  Red Vial Cafetalera
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-medium text-gray-600">Kilómetros Incomunicados</p>
                              <p className="text-2xl font-bold text-gray-900">{Math.round(dept.kmIncomunicados)} Km</p>
                            </div>
                          </div>
                        </div>

                        {/* Estadísticas - Layout Horizontal */}
                        <div className="grid grid-cols-4 gap-3 mb-6">
                          
                          {/* Resumen */}
                          <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg">
                            <h4 className="mb-3 text-xs font-bold text-center text-gray-700 uppercase">Resumen</h4>
                            <div className="space-y-2">
                              <div className="p-2 text-center bg-white border border-gray-300 rounded">
                                <p className="text-xs font-semibold text-gray-600">Municipios</p>
                                <p className="text-xl font-bold text-gray-900">{dept.municipios.size}</p>
                              </div>
                              <div className="p-2 text-center bg-white border border-gray-300 rounded">
                                <p className="text-xs font-semibold text-gray-600">Total Emerg.</p>
                                <p className="text-xl font-bold text-gray-900">{dept.total}</p>
                              </div>
                            </div>
                          </div>

                          {/* Atendidas */}
                          <div className="p-3 text-center bg-green-50 border border-green-300 rounded-lg">
                            <p className="mb-2 text-xs font-bold text-green-700 uppercase">Atendidas</p>
                            <p className="text-3xl font-bold text-green-800 mb-2">{dept.atendidas}</p>
                            <p className="text-xs font-semibold text-green-600">Municipios: {new Set(dept.emergencias.filter(e => e.Estado === 'Atendido').map(e => e.MunicipioFK)).size}</p>
                          </div>

                          {/* En Atención */}
                          <div className="p-3 text-center bg-blue-50 border border-blue-300 rounded-lg">
                            <p className="mb-2 text-xs font-bold text-blue-700 uppercase">En Atención</p>
                            <p className="text-3xl font-bold text-blue-800 mb-2">{dept.enAtencion}</p>
                            <p className="text-xs font-semibold text-blue-600">Municipios: {new Set(dept.emergencias.filter(e => e.Estado === 'En Atención').map(e => e.MunicipioFK)).size}</p>
                          </div>

                          {/* Por Atender */}
                          <div className="p-3 text-center bg-orange-50 border border-orange-300 rounded-lg">
                            <p className="mb-2 text-xs font-bold text-orange-700 uppercase">Por Atender</p>
                            <p className="text-3xl font-bold text-orange-800 mb-2">{dept.porAtender}</p>
                            <p className="text-xs font-semibold text-orange-600">Municipios: {new Set(dept.emergencias.filter(e => e.Estado === 'Por Atender').map(e => e.MunicipioFK)).size}</p>
                          </div>
                        </div>

                        {/* Listado de Emergencias */}
                        <div>
                          <h3 className="pb-2 mb-3 text-base font-bold text-gray-900 border-b-2 border-gray-300">
                            Detalle de Emergencias ({dept.emergencias.length})
                          </h3>
                          
                          <div className="grid grid-cols-2 gap-3">
                            {dept.emergencias.slice(0, 8).map((emergencia, idx) => {
                              const imagenes = obtenerImagenes(emergencia);
                              
                              return (
                                <div key={emergencia.Id} className="p-3 bg-white border-2 border-gray-300 rounded-lg">
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                      <h4 className="text-sm font-bold text-gray-900">{emergencia.NombreTipoEmergencia}</h4>
                                      <div className="flex items-center mt-1 text-gray-600">
                                        <MapPin className="w-3 h-3 mr-1" />
                                        <span className="text-xs font-medium">
                                          {emergencia.NombreMunicipio?.split(',')[0]}
                                        </span>
                                      </div>
                                    </div>
                                    <span className={`px-2 py-1 text-xs font-bold rounded ${
                                      emergencia.Estado === 'Atendido' ? 'bg-green-100 text-green-800 border border-green-300' :
                                      emergencia.Estado === 'En Atención' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                                      'bg-orange-100 text-orange-800 border border-orange-300'
                                    }`}>
                                      {emergencia.Estado}
                                    </span>
                                  </div>

                                  {imagenes.length > 0 && (
                                    <div className="flex gap-1 mt-2">
                                      {imagenes.slice(0, 3).map((url, imgIdx) => (
                                        <img
                                          key={imgIdx}
                                          src={url}
                                          alt={`${idx + 1}-${imgIdx + 1}`}
                                          className="object-cover w-16 h-16 border-2 border-gray-300 rounded"
                                          crossOrigin="anonymous"
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {dept.emergencias.length > 8 && (
                            <div className="p-2 mt-3 text-center bg-gray-100 border border-gray-300 rounded-lg">
                              <p className="text-sm font-semibold text-gray-700">
                                + {dept.emergencias.length - 8} emergencias adicionales
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalBoletines;